const REDACTED_KEYS = new Set([
  'token', 'password', 'secret', 'serversecret', 'appsecret', 'accesstoken', 'refreshtoken',
  'authorization', 'cookie', 'hmac', 'key'
]);
const MAX_STRING_LENGTH = 400;
const MAX_ARRAY_ITEMS = 20;
const MAX_OBJECT_KEYS = 40;

function normalizeKey(key) {
  return String(key || '').replace(/[_.-]/g, '').toLowerCase();
}

function sanitizeString(value) {
  return value
    .replace(/(Bearer\s+)[^\s,]+/gi, '$1[redacted]')
    .replace(/([?&](?:token|password|secret|access_token|refresh_token|authorization|key)=)[^&#\s]+/gi, '$1[redacted]')
    .replace(/((?:token|password|secret|access[_-]?token|refresh[_-]?token|authorization|key)\s*[:=]\s*)[^\s,;]+/gi, '$1[redacted]')
    .replace(/\b[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g, '[redacted]');
}

function sanitize(value, depth = 0) {
  if (value == null || typeof value === 'boolean' || typeof value === 'number') return value;
  if (depth > 3) return '[truncated]';
  if (value instanceof Error) {
    return sanitize({ name: value.name, message: value.message, code: value.code }, depth + 1);
  }
  if (typeof value === 'string') {
    const sanitized = sanitizeString(value);
    return sanitized.length > MAX_STRING_LENGTH ? `${sanitized.slice(0, MAX_STRING_LENGTH)}…` : sanitized;
  }
  if (Array.isArray(value)) return value.slice(0, MAX_ARRAY_ITEMS).map(item => sanitize(item, depth + 1));
  if (typeof value === 'object') {
    const result = {};
    Object.keys(value).slice(0, MAX_OBJECT_KEYS).forEach(key => {
      try {
			result[key] = REDACTED_KEYS.has(normalizeKey(key)) ? '[redacted]' : sanitize(value[key], depth + 1);
      } catch (_) {
        result[key] = '[unavailable]';
      }
    });
    return result;
  }
  return String(value);
}

function getBridge() {
  if (typeof window === 'undefined' || !window.electronAPI || !window.electronAPI.appLogs) return null;
  return window.electronAPI.appLogs;
}

export function appendAppLog(level, args) {
  const bridge = getBridge();
  if (!bridge || typeof bridge.append !== 'function') return;
  const values = Array.isArray(args) ? args : [args];
  const structured = values[0] === '[APP]' && values[1] && typeof values[1] === 'object'
    ? values[1]
    : null;
  Promise.resolve(bridge.append({
    source: 'renderer',
    level: String(level || 'info'),
    scope: structured ? sanitize(structured.scope) : '',
    event: structured ? sanitize(structured.event) : '',
    details: structured ? sanitize(structured.details) : {},
    args: values.map(sanitize)
  })).catch(() => {});
}

export function installAppConsoleBridge() {
  if (typeof window === 'undefined' || window.__appConsoleBridgeInstalled) return;
  if (!getBridge()) return;
  const originalConsole = {};
  ['debug', 'info', 'log', 'warn', 'error'].forEach(level => {
    if (typeof console[level] !== 'function') return;
    originalConsole[level] = console[level].bind(console);
    console[level] = (...args) => {
      originalConsole[level](...args);
      appendAppLog(level, args);
    };
  });
  window.__appConsoleBridgeInstalled = true;
}

export function logAppEvent(scope, event, details = {}) {
  const payload = {
    scope: String(scope || 'app'),
    event: String(event || 'unknown'),
    details: sanitize(details)
  };
  // The console bridge persists this event in Electron and keeps it visible on the Web.
  console.info('[APP]', payload);
  return payload;
}

export function openAppLogDirectory() {
  const bridge = getBridge();
  if (!bridge || typeof bridge.openDirectory !== 'function') {
    return Promise.reject(new Error('当前环境没有本地日志目录'));
  }
  return bridge.openDirectory();
}

export { sanitize as sanitizeAppLogValue };
